import {
  Box,
  Button,
  Typography,
  Card,
  CardMedia,
  styled,
} from '@mui/material';
import {
  Image,
  VideoFile,
  AddPhotoAlternateOutlined,
} from '@mui/icons-material';
import { PresetMediaItem } from 'src/pages/MachineGroupDetails/UpsertPresetForm';

const SelectedMediaTile = styled(Card)(({ theme }) => ({
  position: 'relative',
  aspectRatio: '1',
  width: '80px',
  height: '80px',
  minWidth: '80px',
  minHeight: '80px',
  maxWidth: '80px',
  maxHeight: '80px',
  cursor: 'pointer',
  transition: 'transform 0.2s ease-in-out',
  flexShrink: 0,
  border: `2px solid ${theme.palette.primary.main}`,
  '&:hover': {
    transform: 'scale(1.02)',
  },
}));

interface MediaSelectorProps {
  customerId: string;
  selectedMedia?: PresetMediaItem;
  onSelectMedia: () => void;
  onRemoveMedia: () => void;
}

const getFileName = (media: PresetMediaItem, customerId: string): string => {
  const { id, fullFileUrl } = media;
  if (!fullFileUrl) return '';
  let idIndex = fullFileUrl.indexOf(id);
  if (idIndex === -1) return decodeURIComponent(fullFileUrl);

  const customerIdIndex = fullFileUrl.indexOf(customerId);
  if (customerIdIndex > idIndex) {
    idIndex = customerIdIndex;
  }
  const afterIdIndex = idIndex + id.length;
  const underscoreIndex = fullFileUrl.indexOf('_', afterIdIndex);

  const fileName =
    underscoreIndex !== -1
      ? fullFileUrl.substring(underscoreIndex + 1)
      : fullFileUrl;

  return decodeURIComponent(fileName);
};

function MediaSelector({
  customerId,
  selectedMedia,
  onSelectMedia,
  onRemoveMedia,
}: MediaSelectorProps) {
  const renderMediaPreview = (media: PresetMediaItem) => {
    if (!media.fullFileUrl) return null;
    const fileName = getFileName(media, customerId);
    const isVideo =
      media.fullFileUrl.toLowerCase().includes('.mp4') ||
      fileName.toLowerCase().endsWith('.mp4');
    const isImage =
      media.fullFileUrl.toLowerCase().includes('.jpg') ||
      media.fullFileUrl.toLowerCase().includes('.jpeg') ||
      fileName.toLowerCase().endsWith('.jpg') ||
      fileName.toLowerCase().endsWith('.jpeg');

    if (media.thumbnailUrl) {
      return (
        <CardMedia
          component="img"
          src={media.thumbnailUrl}
          alt={fileName}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            backgroundColor: 'grey.50',
          }}
        />
      );
    }
    if (isVideo) {
      return <VideoFile sx={{ fontSize: 24, color: 'grey.500' }} />;
    }
    if (isImage) {
      return <Image sx={{ fontSize: 24, color: 'grey.500' }} />;
    }
    return <Image sx={{ fontSize: 24, color: 'grey.500' }} />;
  };

  if (
    selectedMedia &&
    selectedMedia.id &&
    selectedMedia.id !== '00000000-0000-0000-0000-000000000000'
  ) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <SelectedMediaTile onClick={onSelectMedia}>
          {renderMediaPreview(selectedMedia)}
        </SelectedMediaTile>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
            Selected Media:
          </Typography>
          <Typography variant="caption">
            {getFileName(selectedMedia, customerId)}
          </Typography>
          <Button size="small" color="error" onClick={onRemoveMedia}>
            Remove
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Button
      variant="outlined"
      startIcon={<AddPhotoAlternateOutlined />}
      onClick={onSelectMedia}
      sx={{ width: 'fit-content' }}
    >
      Select Media
    </Button>
  );
}

export default MediaSelector;
